import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ModelArena.css";
import { apiFetch } from "./utils/apiClient";

const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000/api"
    : "https://artikaorder-backend.onrender.com/api";

const MODEL_META = {
  chatgpt: { label: "ChatGPT", tag: "OpenAI", emoji: "🟢", className: "card-chatgpt" },
  claude: { label: "Claude", tag: "Anthropic", emoji: "🟠", className: "card-claude" },
  gemini: { label: "Gemini", tag: "Google", emoji: "🔵", className: "card-gemini" },
};

const REVEAL_ORDER = ["chatgpt", "claude", "gemini"];

const timeAgo = (iso) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "الآن";
  if (mins < 60) return `قبل ${mins} د`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `قبل ${hours} س`;
  const days = Math.floor(hours / 24);
  return `قبل ${days} يوم`;
};

const ModelArena = () => {
  const navigate = useNavigate();
  const username = localStorage.getItem("username") || "admin";

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [revealMap, setRevealMap] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectionPending, setSelectionPending] = useState(false);

  const textareaRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const revealTimers = useRef([]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;800;900&family=Space+Grotesk:wght@400;500;700&display=swap";
    document.head.appendChild(link);

    loadConversations();

    return () => {
      document.head.removeChild(link);
      revealTimers.current.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, revealMap]);

  const loadConversations = async () => {
    try {
      const res = await apiFetch(
        `${API_BASE}/model-arena/conversations?owner=${encodeURIComponent(username)}`
      );
      const data = await res.json();
      if (res.ok) setConversations(data.conversations || []);
    } catch (err) {
      console.error("Load conversations error:", err);
    }
  };

  const revealTurnInstantly = (turn) => {
    setRevealMap((prev) => ({
      ...prev,
      [turn.id]: { chatgpt: true, claude: true, gemini: true },
    }));
  };

  const revealTurnWithAnimation = (turn) => {
    REVEAL_ORDER.forEach((key, index) => {
      const timer = setTimeout(() => {
        setRevealMap((prev) => ({
          ...prev,
          [turn.id]: { ...(prev[turn.id] || {}), [key]: true },
        }));
      }, 220 * (index + 1));
      revealTimers.current.push(timer);
    });
  };

  const loadConversation = async (id) => {
    try {
      setError("");
      const res = await apiFetch(
        `${API_BASE}/model-arena/conversations/${id}?owner=${encodeURIComponent(username)}`
      );
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "تعذر تحميل المحادثة");

      const loadedTurns = data.turns || [];
      setTurns(loadedTurns);
      setActiveId(id);
      setMessage("");
      setSidebarOpen(false);

      const map = {};
      loadedTurns.forEach((t) => {
        map[t.id] = { chatgpt: true, claude: true, gemini: true };
      });
      setRevealMap(map);
    } catch (err) {
      console.error("Load conversation error:", err);
      setError(err.message);
    }
  };

  const startNewConversation = () => {
    setActiveId(null);
    setTurns([]);
    setMessage("");
    setError("");
    setRevealMap({});
    setSidebarOpen(false);
    textareaRef.current?.focus();
  };

  const lastTurn = turns[turns.length - 1];
  const mustSelectFirst = Boolean(lastTurn && !lastTurn.selectedModel);

  const sendMessage = async () => {
    if (!message.trim() || sending || mustSelectFirst || selectionPending) return;

    setSending(true);
    setError("");

    try {
      let convId = activeId;

      if (!convId) {
        const createRes = await apiFetch(`${API_BASE}/model-arena/conversations`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ owner: username }),
        });
        const createData = await createRes.json();
        if (!createRes.ok) throw new Error(createData.error || "تعذر إنشاء محادثة جديدة");
        convId = createData.id;
        setActiveId(convId);
      }

      const res = await apiFetch(`${API_BASE}/model-arena/conversations/${convId}/turns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ owner: username, message }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "حدث خطأ أثناء إرسال السؤال");

      setTurns((prev) => [...prev, data.turn]);
      revealTurnWithAnimation(data.turn);
      setMessage("");
      loadConversations();
    } catch (err) {
      console.error("Send message error:", err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const selectModel = async (turnId, modelKey) => {
    const turn = turns.find((t) => t.id === turnId);
    if (!turn || !turn.responses?.[modelKey]?.ok) return;
    if (!activeId) return;

    const previous = turn.selectedModel;
    setTurns((prev) =>
      prev.map((t) => (t.id === turnId ? { ...t, selectedModel: modelKey } : t))
    );
    setSelectionPending(true);

    try {
      const res = await apiFetch(
        `${API_BASE}/model-arena/conversations/${activeId}/turns/${turnId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ owner: username, selectedModel: modelKey }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "تعذر حفظ الاختيار");
      }
    } catch (err) {
      console.error("Select model error:", err);
      setTurns((prev) =>
        prev.map((t) => (t.id === turnId ? { ...t, selectedModel: previous } : t))
      );
      setError(err.message);
    } finally {
      setSelectionPending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const renderDecidedAnswer = (turn) => {
    const meta = MODEL_META[turn.selectedModel];
    const result = turn.responses?.[turn.selectedModel];
    if (!meta || !result) return null;

    return (
      <div className={`arena-answer-bubble ${meta.className}`}>
        <div className="arena-answer-header">
          <span className="arena-card-emoji">{meta.emoji}</span>
          <span className="arena-answer-name">{meta.label}</span>
          <span className="arena-card-tag">{result.model || meta.tag}</span>

          <div className="arena-switch-tabs">
            {REVEAL_ORDER.map((key) => {
              const optionResult = turn.responses?.[key];
              const optionMeta = MODEL_META[key];
              const isActive = key === turn.selectedModel;
              const isDisabled = !optionResult?.ok;

              return (
                <button
                  key={key}
                  type="button"
                  className={`arena-switch-tab ${isActive ? "is-active" : ""} ${
                    isDisabled ? "is-disabled" : ""
                  }`}
                  title={optionMeta.label}
                  disabled={isDisabled}
                  onClick={() => selectModel(turn.id, key)}
                >
                  {optionMeta.emoji}
                </button>
              );
            })}
          </div>
        </div>

        <p className="arena-answer-text" dir="auto">
          {result.text}
        </p>

        <button
          className="arena-copy-btn arena-answer-copy"
          onClick={() => handleCopy(result.text)}
          type="button"
        >
          📋 نسخ
        </button>
      </div>
    );
  };

  const renderCard = (turn, key) => {
    const meta = MODEL_META[key];
    const result = turn.responses?.[key];
    const isRevealed = Boolean(revealMap[turn.id]?.[key]) && result;
    const isSelected = turn.selectedModel === key;
    const isUnavailable = isRevealed && result.ok === false;
    const isSelectable = isRevealed && result.ok !== false;

    return (
      <div
        key={key}
        className={[
          "arena-card",
          meta.className,
          isRevealed ? "is-revealed" : "",
          isSelected ? "is-selected" : "",
          isRevealed && !isSelected && !isUnavailable ? "is-dimmed" : "",
          isSelectable ? "is-selectable" : "",
        ].join(" ")}
        onClick={() => isSelectable && selectModel(turn.id, key)}
      >
        <div className="arena-card-header">
          <span className="arena-card-emoji">{meta.emoji}</span>
          <div>
            <div className="arena-card-title">{meta.label}</div>
            <div className="arena-card-tag">{result?.model || meta.tag}</div>
          </div>

          {isSelected && <span className="arena-card-badge">✓ المعتمد</span>}

          {isRevealed && typeof result.durationMs === "number" && !isSelected && (
            <span className="arena-card-time">{(result.durationMs / 1000).toFixed(1)}s</span>
          )}
        </div>

        <div className="arena-card-body">
          {!isRevealed ? (
            <div className="arena-typing">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
              <span className="arena-typing-label">{meta.label} يفكر...</span>
            </div>
          ) : (
            <>
              {isUnavailable && <div className="arena-card-warning">هذه النتيجة لم تكتمل</div>}
              <p className="arena-card-text" dir="auto">
                {result.text}
              </p>
            </>
          )}
        </div>

        {isRevealed && !isUnavailable && (
          <div className="arena-card-footer">
            {!isSelected && <span className="arena-card-hint">اضغط للاختيار</span>}
            <button
              className="arena-copy-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(result.text);
              }}
              type="button"
            >
              📋 نسخ
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="arena-page" dir="rtl">
      <div className="arena-bg">
        <span className="arena-blob blob-chatgpt" />
        <span className="arena-blob blob-claude" />
        <span className="arena-blob blob-gemini" />
        <div className="arena-grid-overlay" />
      </div>

      <button className="arena-back-btn" onClick={() => navigate("/admin")} type="button">
        ⬅ رجوع للنظام
      </button>

      <button
        className="arena-sidebar-toggle"
        onClick={() => setSidebarOpen((v) => !v)}
        type="button"
      >
        ☰
      </button>

      <div className="arena-shell">
        <aside className={`arena-sidebar ${sidebarOpen ? "is-open" : ""}`}>
          <button className="arena-new-btn" onClick={startNewConversation} type="button">
            ✨ محادثة جديدة
          </button>

          <div className="arena-conv-list">
            {conversations.length === 0 && (
              <div className="arena-conv-empty">ماكو محادثات بعد</div>
            )}

            {conversations.map((c) => (
              <button
                key={c.id}
                className={`arena-conv-item ${activeId === c.id ? "is-active" : ""}`}
                onClick={() => loadConversation(c.id)}
                type="button"
              >
                <div className="arena-conv-title" dir="auto">
                  {c.title}
                </div>
                <div className="arena-conv-meta">{timeAgo(c.updatedAt)}</div>
              </button>
            ))}
          </div>
        </aside>

        <div className="arena-main">
          {turns.length === 0 ? (
            <header className="arena-header">
              <div className="arena-orbit">
                <div className="orbit-core">🧠</div>
                <div className="orbit-ring ring-1">
                  <span className="orbit-dot dot-chatgpt" />
                </div>
                <div className="orbit-ring ring-2">
                  <span className="orbit-dot dot-claude" />
                </div>
                <div className="orbit-ring ring-3">
                  <span className="orbit-dot dot-gemini" />
                </div>
              </div>

              <h1 className="arena-title">🌪️ دوّامة العباقرة</h1>
              <p className="arena-subtitle">
                سؤال وحد... وثلاث عقول عملاقة تلف وتدور عليك بأحلى جواب 😎
              </p>
              <p className="arena-caption">ChatGPT × Claude × Gemini — بمكان وحد</p>
            </header>
          ) : (
            <div className="arena-topbar">
              <span className="arena-topbar-emoji">🌪️</span>
              <span className="arena-topbar-title">دوّامة العباقرة</span>
            </div>
          )}

          <div className="arena-transcript">
            {turns.map((turn) => (
              <div className="arena-turn" key={turn.id}>
                <div className="arena-user-bubble" dir="auto">
                  {turn.userMessage}
                </div>

                {turn.selectedModel ? (
                  renderDecidedAnswer(turn)
                ) : (
                  <div className="arena-results-grid">
                    {REVEAL_ORDER.map((key) => renderCard(turn, key))}
                  </div>
                )}
              </div>
            ))}
            <div ref={transcriptEndRef} />
          </div>

          {error && <div className="arena-error">⚠️ {error}</div>}

          {mustSelectFirst && (
            <div className="arena-select-hint">👆 اختر أفضل جواب علشان تكمل الحديث</div>
          )}

          <div className="arena-input-shell">
            <textarea
              ref={textareaRef}
              className="arena-textarea"
              placeholder={
                mustSelectFirst ? "اختر جواب أولاً حتى تقدر تكمل..." : "اكتب سؤالك هنا... واضغط Enter"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={sending || mustSelectFirst || selectionPending}
            />

            <div className="arena-input-actions">
              <span className="arena-conv-status">
                {activeId ? "محادثة مستمرة" : "محادثة جديدة"}
              </span>

              <button
                className="arena-send-btn"
                onClick={sendMessage}
                disabled={sending || !message.trim() || mustSelectFirst || selectionPending}
                type="button"
              >
                {sending ? (
                  <span className="arena-send-loading">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </span>
                ) : (
                  <>ابعث 🚀</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelArena;
